import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';

const statusDot = (status) => {
  const map = {
    UP_TO_DATE: '#059669',
    OUTDATED: '#d97706',
    NO_DATA: '#dc2626',
  };
  return map[status] || '#9ca3af';
};

function SectorsTable({ sectors, onViewDetails, onRefreshSector }) {
  const { t } = useTranslation();

  const statusLabel = (status) => {
    const map = {
      UP_TO_DATE: t('sectorsManagement.status.upToDate'),
      OUTDATED: t('sectorsManagement.status.outdated'),
      NO_DATA: t('sectorsManagement.status.noData'),
    };
    return map[status] || status;
  };

  const columns = [
    { key: 'id', label: '#', minW: 40, align: 'center' },
    { key: 'code', label: t('sectorsManagement.table.code'), minW: 70 },
    { key: 'name', label: t('sectorsManagement.table.nameEn'), minW: 120 },
    { key: 'nameVi', label: t('sectorsManagement.table.nameVi'), minW: 120 },
    { key: 'stocks', label: t('sectorsManagement.table.stockCount'), minW: 60, align: 'center' },
    { key: 'days', label: t('sectorsManagement.table.dataDays'), minW: 60, align: 'center' },
    { key: 'latest', label: t('sectorsManagement.table.latestDate'), minW: 90 },
    { key: 'status', label: t('sectorsManagement.table.status'), minW: 80 },
    { key: 'actions', label: '', minW: 40, align: 'center' },
  ];

  return (
    <Box sx={{
      bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
      borderRadius: 2.5, overflow: 'hidden',
    }}>
      <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.85rem' }}>
          {t('sectorsManagement.title')}
        </Typography>
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  align={col.align || 'left'}
                  sx={{
                    fontWeight: 700, fontSize: '0.72rem', py: 1, px: 1.25,
                    minWidth: col.minW, whiteSpace: 'nowrap', bgcolor: 'background.paper',
                    borderBottom: '2px solid', borderColor: 'divider',
                    textTransform: 'uppercase', letterSpacing: 0.3, color: 'text.secondary',
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sectors.map((sector) => (
              <TableRow
                key={sector.sector_id}
                hover
                onClick={() => onViewDetails(sector.code)}
                sx={{ cursor: 'pointer', '&:last-child td': { borderBottom: 0 } }}
              >
                <TableCell align="center" sx={{ py: 1, px: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem', fontFeatureSettings: '"tnum"' }}>
                    {sector.sector_id}
                  </Typography>
                </TableCell>
                <TableCell sx={{ py: 1, px: 1.25 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'primary.main' }}>
                    {sector.code}
                  </Typography>
                </TableCell>
                <TableCell sx={{ py: 1, px: 1.25 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{sector.name}</Typography>
                </TableCell>
                <TableCell sx={{ py: 1, px: 1.25 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{sector.name_vi}</Typography>
                </TableCell>
                <TableCell align="center" sx={{ py: 1, px: 1 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.78rem', fontFeatureSettings: '"tnum"' }}>
                    {sector.stock_count || 0}
                  </Typography>
                </TableCell>
                <TableCell align="center" sx={{ py: 1, px: 1 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.78rem', fontFeatureSettings: '"tnum"' }}>
                    {sector.data_days || 0}
                  </Typography>
                </TableCell>
                <TableCell sx={{ py: 1, px: 1.25 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem' }}>
                    {sector.latest_date || '—'}
                  </Typography>
                </TableCell>
                <TableCell sx={{ py: 1, px: 1.25 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box sx={{
                      width: 7, height: 7, borderRadius: '50%',
                      bgcolor: statusDot(sector.data_status),
                    }} />
                    <Typography variant="body2" sx={{
                      fontSize: '0.75rem', fontWeight: 600,
                      color: statusDot(sector.data_status),
                    }}>
                      {statusLabel(sector.data_status)}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="center" sx={{ py: 1, px: 0.5 }} onClick={(e) => e.stopPropagation()}>
                  {sector.has_mapping && (
                    <Tooltip title={t('sectorsManagement.table.refreshData')}>
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); onRefreshSector(sector.code); }}
                        sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                      >
                        <RefreshIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default SectorsTable;

